import { createRouter, createWebHistory } from 'vue-router'
import authService from '@/services/authService'
import heartbeatService from '@/services/heartbeatService'

// Auth Views
import LoginView from '@/views/LoginView.vue'
import RegisterView from '@/views/RegisterView.vue'

// Dashboard Layout
import DashboardView from '@/views/DashboardView.vue'

// Dashboard Pages
import HomeView from '@/views/HomeView.vue'
import ProjectsView from '@/views/Project/ProjectsView.vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresGuest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: { requiresGuest: true }
  },
  {
    path: '/forgot-password',
    name: 'Forgot Password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/reset-password',
    name: 'Reset Password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: DashboardView,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'fooldal',
        name: 'Home',
        component: HomeView,
        meta: { requiresAuth: true }
      },
      {
        path: 'projects',
        name: 'Projects',
        component: ProjectsView,
        meta: { requiresAuth: true }
      },
      {
        path: 'projects/create',
        name: 'Create Project',
        component: () => import('@/views/Project/CreateProjectView.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'projects/:id',
        name: 'View Project',
        component: () => import('@/views/Project/ViewProjectView.vue'),
        meta: { requiresAuth: true }
      },
       {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/views/Invoices/InvoicesIndex.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'settings/me',
        name: 'User Settings',
        component: () => import('@/views/Settings/UserSettingsView.vue'),
        meta: { requiresAuth: true}
      },
      {
        path: 'settings/company',
        name: 'Company Settings',
        component: () => import('@/views/Settings/CompanySettingsView.vue'),
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      // Ha /api-val kezdődik az útvonal, ne redirect-elje
      if (to.path.startsWith('/api')) {
        return false
      }
      return '/'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const isAuthenticated = authService.isAuthenticated()

  // Ha a gyökér URL-t nyitja meg ("/"), irányítsuk a megfelelő helyre
  if (to.path === '/') {
    if (isAuthenticated) {
      next('/fooldal') // Authentikált felhasználó a főoldalra
    } else {
      next('/login') // Nem authentikált felhasználó a login oldalra
    }
    return
  }

  // Ha authentikáció szükséges, de nincs bejelentkezve
  if (to.meta.requiresAuth && !isAuthenticated) {
    // Heartbeat leállítása
    heartbeatService.stop()
    
    // Mentjük el a kívánt útvonalat a sessionStorage-ba
    if (to.path !== '/') {
      sessionStorage.setItem('redirectAfterLogin', to.fullPath)
    }
    next('/login')
    return
  }

  // Role check
  if (to.meta.requiresRoles && isAuthenticated) {
    const hasRequiredRole = to.meta.requiresRoles.some(role => authService.hasRole(role))
    if (!hasRequiredRole) {
      next('/fooldal') // Redirect to home if no required role
      return
    }
  }

  // Teszt felhasználók ellenőrzése
  if (to.meta.requiresTest && isAuthenticated) {
    if (!authService.isTester()) {
      next('/fooldal') // Redirect to home if not a tester
      return
    }
  }

  // Ha vendég oldal, de be van jelentkezve
  if (to.meta.requiresGuest && isAuthenticated) {
    next('/')
    return
  }

  next()
})

// Oldal változások követése
router.afterEach((to, from) => {
  // Heartbeat értesítése az oldal változásról
  if (authService.isAuthenticated()) {
    setTimeout(() => {
      heartbeatService.sendImmediateHeartbeat()
    }, 100)
  }
})

export default router